import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { StatCard } from 'src/components/actionCards';
import { REPORT_METRIC_ACCENTS } from 'src/components/actionCards/actionCardColors';

const KPI_CONFIGS = [
    {
        key: 'totalLost',
        label: 'Total Lost',
        icon: 'mdi:account-remove',
        tooltip: 'Total number of leads archived/lost in this period.',
        showMom: true,
    },
    {
        key: 'topReason',
        label: 'Top Reason',
        icon: 'mdi:alert-circle',
        isText: true,
        tooltip: 'The most common reason leads were lost.',
    },
    {
        key: 'avgDaysToArchive',
        label: 'Avg Days to Archive',
        icon: 'mdi:clock-outline',
        suffix: ' days',
        tooltip: 'Average number of days from lead creation to archive.',
    },
    {
        key: 'lostRevenue',
        label: 'Lost Revenue',
        icon: 'mdi:currency-gbp',
        format: 'currency',
        tooltip: 'Estimated revenue lost from archived leads.',
    },
    {
        key: 'archiveRate',
        label: 'Archive Rate',
        icon: 'mdi:chart-line-variant',
        suffix: '%',
        // Defined precisely: the numerator is leads ARCHIVED in the period (any
        // creation date), the denominator is NEW leads created in the period, so
        // the ratio can exceed 100%.
        tooltip: 'Archived leads ÷ new leads created in this period. Archived leads may have been created earlier, so this can exceed 100%.',
    },
];

const formatCurrency = (value) => {
    if (value >= 1000000) return `\u00A3${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `\u00A3${(value / 1000).toFixed(1)}K`;
    return `\u00A3${value}`;
};

export default function LostLeadsKPICards({ kpis }) {
    const { momChange } = kpis;

    return (
        <Box sx={{ display: 'flex', gap: '16px', mb: 3, flexWrap: 'wrap' }}>
            {KPI_CONFIGS.map((config) => {
                const rawValue = kpis[config.key];
                // Treat null as "unknown" so a KPI whose underlying request
                // rejected can render "N/A" instead of silently showing 0
                // (the Lost Leads archive-rate failure mode — the backend
                // /reports/leads/ call is allSettled and can reject
                // independently). undefined falls through to the 0 path
                // to preserve the existing default-to-0 behaviour for
                // keys that have always been optional.
                const isUnknown = rawValue === null;
                let displayValue;
                if (config.isText) {
                    displayValue = kpis[config.key] || 'N/A';
                } else if (isUnknown) {
                    displayValue = 'N/A';
                } else if (config.format === 'currency') {
                    displayValue = formatCurrency(rawValue || 0);
                } else if (config.key === 'archiveRate' && rawValue > 100) {
                    displayValue = '100%+';
                } else {
                    displayValue = `${rawValue || 0}${config.suffix || ''}`;
                }
                const chips = config.key === 'archiveRate' && kpis.newLeadsCount !== undefined && kpis.newLeadsCount !== null && kpis.archivedCount !== undefined
                    ? [{ key: 'counts', text: `${kpis.archivedCount} archived ÷ ${kpis.newLeadsCount} new`, type: 'info', showDot: false }]
                    : undefined;

                // Period-over-period change. Lost leads going UP is bad, so the pill is inverted
                // (up = amber). Never red.
                let delta;
                if (config.showMom && momChange) {
                    const momValue = momChange.direction === 'flat' ? 0 : parseFloat(momChange.pct);
                    delta = {
                        value: Number.isFinite(momValue) ? momValue : null,
                        inverted: true,
                        tooltip: `vs previous period: ${momChange.count >= 0 ? '+' : ''}${momChange.count}`,
                    };
                }

                return (
                    <Box key={config.key} data-testid="kpi-card" sx={{ flex: 1, minWidth: '150px', display: 'flex' }}>
                        <StatCard
                            size="sm"
                            label={config.label}
                            value={displayValue}
                            accentColor={REPORT_METRIC_ACCENTS.lost}
                            icon={config.icon}
                            tooltip={config.tooltip}
                            delta={delta}
                            chips={chips}
                            valueSx={config.isText ? { fontSize: '18px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 } : { whiteSpace: 'nowrap' }}
                        />
                    </Box>
                );
            })}
        </Box>
    );
}

LostLeadsKPICards.propTypes = {
    kpis: PropTypes.shape({
        totalLost: PropTypes.number,
        topReason: PropTypes.string,
        avgDaysToArchive: PropTypes.number,
        lostRevenue: PropTypes.number,
        // number = known rate; null = unknown (underlying count request
        // rejected). Component renders "N/A" in the null case.
        archiveRate: PropTypes.number,
        archivedCount: PropTypes.number,
        newLeadsCount: PropTypes.number,
        momChange: PropTypes.shape({
            count: PropTypes.number,
            pct: PropTypes.string,
            direction: PropTypes.string,
        }),
    }),
};

LostLeadsKPICards.defaultProps = {
    kpis: {
        totalLost: 0,
        topReason: 'N/A',
        avgDaysToArchive: 0,
        lostRevenue: 0,
        archiveRate: 0,
        momChange: null,
    },
};
